// src/ManageDoctors.js

import React, { useState, useEffect } from 'react';
import { db, functions } from './firebase'; 
import { httpsCallable } from "firebase/functions"; 
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, setDoc } from 'firebase/firestore'; 
import { DoctorModal } from './DoctorModal';
import { useUser } from './UserContext';
import { logAction } from './loggingService';
import Select from 'react-select';
import { AssociateAccountModal } from './AssociateAccountModal'; 
import { ConfirmationModal } from './Modals'; 
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom'; // 🎯 FIX: Ci serve per tornare alla home

function ManageDoctors({ isProfileMode }) { // 🎯 FIX: Aggiunta la prop isProfileMode
    const { userProfile } = useUser();
    const navigate = useNavigate(); // 🎯 FIX: Hook di navigazione
    
    // --- CLOUD FUNCTIONS ---
    const createDoctorAccountFn = httpsCallable(functions, 'createDoctorAccount'); 
    const changeUserEmailFn = httpsCallable(functions, 'changeUserEmail');
    const resetUserPasswordFn = httpsCallable(functions, 'resetUserPassword');
    const linkExistingAccountToDoctorFn = httpsCallable(functions, 'linkExistingAccountToDoctor'); // 🆕
    // --- FINE CF ---

    const permissionLevel = userProfile?.permissions?.rooms || 'none';
    const canEdit = permissionLevel === 'completo' || permissionLevel === 'semi-completo';
    const canDelete = permissionLevel === 'completo';
    
    const [doctors, setDoctors] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [services, setServices] = useState([]); // 🆕 Aggiunto stato per le prestazioni
    const [allStaff, setAllStaff] = useState([]); // 🆕 Serve per "Associa Account Esistente"

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDoctor, setEditingDoctor] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSpecialties, setSelectedSpecialties] = useState([]);
    
    // --- STATI PER L'ASSOCIAZIONE ACCOUNT ---
    const [associationModal, setAssociationModal] = useState({ isOpen: false, doctor: null });
    const [resetPwdModal, setResetPwdModal] = useState({ isOpen: false, doctor: null });

    const fetchData = async () => {
        if (!userProfile || !userProfile.centerId) {
            setDoctors([]); setSpecialties([]); setServices([]); setAllStaff([]);
            return;
        }
        const centerId = userProfile.centerId;
        
        // 🆕 Aggiunto fetch parallelo per i "services" e per lo staff (serve per
        // popolare la lista "Associa Account Esistente")
        const [specialtiesSnapshot, doctorsSnapshot, servicesSnapshot, staffSnapshot] = await Promise.all([
            getDocs(collection(db, 'centers', centerId, 'specialties')),
            getDocs(collection(db, 'centers', centerId, 'doctors')),
            getDocs(collection(db, 'centers', centerId, 'services')),
            getDocs(collection(db, 'centers', centerId, 'staff'))
        ]);
        
        const specialtiesList = specialtiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSpecialties(specialtiesList);

        const servicesList = servicesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setServices(servicesList); // 🆕 Salviamo le prestazioni in stato

        setAllStaff(staffSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); // 🆕

        const doctorsList = doctorsSnapshot.docs.map(doc => {
            const data = doc.data();
            const specialtyNames = (data.specialtyIds || [])
                .map(id => specialtiesList.find(s => s.id === id)?.name)
                .filter(Boolean);
            return { id: doc.id, ...data, specialtyNames }; 
        });

        setDoctors(doctorsList.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '')));
    };

    useEffect(() => {
        fetchData();
    }, [userProfile]);

    // 🎯 FIX CHIRURGICO: Se siamo in modalità Profilo Personale, auto-selezioniamo il medico loggato e apriamo il modale
    useEffect(() => {
        if (isProfileMode && doctors.length > 0) {
            const myDoctorProfile = doctors.find(d => d.authUid === userProfile.uid);
            if (myDoctorProfile) {
                setEditingDoctor(myDoctorProfile);
                setIsModalOpen(true);
            } else {
                toast.error("Profilo medico non trovato.");
                navigate('/'); // Fallback di sicurezza
            }
        }
    }, [isProfileMode, doctors, userProfile.uid, navigate]);

    const handleSaveDoctor = async (doctorData) => {
        // 🆕 Aggiunti i campi clinici nel payload di salvataggio
        const dataToSave = {
            firstName: doctorData.firstName,
            lastName: doctorData.lastName,
            exams: doctorData.exams,
            specialtyIds: doctorData.specialtyIds,
            email: doctorData.email,
            pIva: doctorData.pIva,
            iban: doctorData.iban,
            colore: doctorData.colore || doctorData.color || "#3b82f6", // 🆕 Aggiunto il colore
            isBookable: doctorData.isBookable,
            percentualeManuale: doctorData.percentualeManuale,
            percentualeStrumentale: doctorData.percentualeStrumentale,
            prestazioniAssegnate: doctorData.prestazioniAssegnate,
            eccezioniListino: doctorData.eccezioniListino,
            centerId: userProfile.centerId,
            categorieRefertazione: doctorData.categorieRefertazione,
            paymentType: doctorData.paymentType, // 🆕 Propaghiamo i nuovi campi dei compensi!
            baseRate: doctorData.baseRate,
            percentageExceptions: doctorData.percentageExceptions
        };

        Object.keys(dataToSave).forEach(key => dataToSave[key] === undefined && delete dataToSave[key]);

        if (doctorData.id) {
            await updateDoc(doc(db, 'centers', userProfile.centerId, 'doctors', doctorData.id), dataToSave);
            await logAction(userProfile, `ha modificato i dati del medico: ${doctorData.firstName} ${doctorData.lastName}`);
        } else {
            await addDoc(collection(db, 'centers', userProfile.centerId, 'doctors'), dataToSave);
            await logAction(userProfile, `ha creato il medico: ${doctorData.firstName} ${doctorData.lastName}`);
        }
        setIsModalOpen(false);
        fetchData();
    };

    const handleDeleteDoctor = async (doctorId) => {
        const doctorToDelete = doctors.find(d => d.id === doctorId);
        if (window.confirm("Sei sicuro di voler eliminare questo medico? L'eliminazione non rimuoverà il suo account di accesso.")) {
            await deleteDoc(doc(db, 'centers', userProfile.centerId, 'doctors', doctorId));
            if (doctorToDelete) {
                await logAction(userProfile, `ha eliminato il medico: ${doctorToDelete.firstName} ${doctorToDelete.lastName}`);
            }
            fetchData();
        }
    };
    
    // ... LOGICA ACCOUNT INVARIATA ...
    const handleCreateAccount = async (email, password) => {
        const doctorData = associationModal.doctor;
        if (!doctorData) return;
        const promise = createDoctorAccountFn({ 
            email: email, password: password, doctorDocId: doctorData.id, 
            centerId: userProfile.centerId, doctorData: { ...doctorData, email: email } 
        });
        toast.promise(promise, { pending: 'Creazione account...', success: `Account creato con successo!`, error: 'Errore.'});
        try { await promise; fetchData(); } catch (error) { console.error(error); } finally { setAssociationModal({ isOpen: false, doctor: null }); }
    };

    // 🆕 Collega il medico a un account GIÀ ESISTENTE (es. il Proprietario che
    // è anche medico): nessun nuovo login, ruolo/permessi dell'account restano invariati.
    const handleLinkExistingAccount = async (existingUid) => {
        const doctorData = associationModal.doctor;
        if (!doctorData) return;
        const promise = linkExistingAccountToDoctorFn({
            existingUid, doctorDocId: doctorData.id, centerId: userProfile.centerId,
        });
        toast.promise(promise, { pending: 'Associazione in corso...', success: 'Account associato con successo!', error: 'Errore.' });
        try { await promise; fetchData(); } catch (error) { console.error(error); } finally { setAssociationModal({ isOpen: false, doctor: null }); }
    };
    
    const handleChangeEmail = async (doctor) => {
        const newEmail = prompt(`Inserisci la nuova email per ${doctor.firstName} ${doctor.lastName}:`, doctor.email || "");
        if (newEmail && newEmail.trim() !== "") {
            if (!doctor.authUid) { toast.error("Account non associato."); return; }
            const promise = changeUserEmailFn({ uid: doctor.authUid, newEmail: newEmail.trim(), centerId: userProfile.centerId });
            toast.promise(promise, { pending: 'Aggiornamento...', success: 'Email aggiornata!', error: 'Errore.' });
            try {
                await promise;
                await updateDoc(doc(db, 'centers', userProfile.centerId, 'doctors', doctor.id), { email: newEmail.trim() });
                fetchData();
            } catch (error) {}
        }
    };

    const handleResetPassword = async (doctor) => {
        if (!doctor.authUid) { toast.error("Account non associato."); return; }
        try {
            await toast.promise(resetUserPasswordFn({ uid: doctor.authUid, centerId: userProfile.centerId }), { pending: 'Reset in corso...', success: `Password resettata a 123456`, error: 'Errore.' });
        } catch(error) { console.error(error); } finally { setResetPwdModal({isOpen: false, doctor: null}); }
    };

    const handleEditClick = (doctor) => { setEditingDoctor(doctor); setIsModalOpen(true); };
    const handleNewClick = () => { setEditingDoctor(null); setIsModalOpen(true); };

    const handleSpecialtyToggle = (specId) => {
        setSelectedSpecialties(prev => prev.includes(specId) ? prev.filter(id => id !== specId) : [...prev, specId]);
    };

    const specialtyOptions = specialties.map(spec => ({ value: spec.id, label: spec.name })).sort((a, b) => a.label.localeCompare(b.label));

    const filteredDoctors = doctors.filter(doctor => {
        const searchKeywords = searchTerm.toLowerCase().split(' ').filter(k => k);
        if (searchKeywords.length > 0) {
            const fullName = `${doctor.firstName} ${doctor.lastName}`.toLowerCase();
            if (!searchKeywords.every(keyword => fullName.includes(keyword))) return false;
        }
        if (selectedSpecialties.length > 0) {
            const selectedIds = selectedSpecialties.map(s => s.value);
            const hasSelectedSpecialty = selectedIds.some(specId => (doctor.specialtyIds || []).includes(specId));
            if (!hasSelectedSpecialty) return false;
        }
        return true;
    });

    // 🎯 FIX CHIRURGICO: Intercettiamo la UI se siamo nel "Mio Profilo"
    if (isProfileMode) {
        return (
            <>
                {isModalOpen ? (
                    <DoctorModal 
                        doctorData={editingDoctor} 
                        allSpecialties={specialties} 
                        allServices={services} 
                        // Quando salva, esegue il salvataggio base e poi torna alla Home
                        onSave={async (data) => {
                            await handleSaveDoctor(data);
                            toast.success("Profilo aggiornato!");
                            navigate('/'); 
                        }} 
                        onClose={() => { setIsModalOpen(false); navigate('/'); }} 
                        isProfileMode={true} // 🎯 Flag ESSENZIALE che passeremo a DoctorModal.js per bloccare i campi
                    /> 
                ) : (
                    <div style={{ textAlign: 'center', marginTop: '50px', color: '#6c757d' }}>
                        Caricamento profilo in corso...
                    </div>
                )}
            </>
        );
    }

    // --- UI STANDARD (Gestione per gli Admin) ---
    return (
        <>
            {isModalOpen && ( 
                <DoctorModal 
                    doctorData={editingDoctor} 
                    allSpecialties={specialties} 
                    allServices={services} 
                    onSave={handleSaveDoctor} 
                    onClose={() => setIsModalOpen(false)} 
                /> 
            )}
            
            {associationModal.isOpen && (
                <AssociateAccountModal
                    personName={`${associationModal.doctor.firstName} ${associationModal.doctor.lastName}`}
                    existingStaff={allStaff}
                    onConfirmNew={handleCreateAccount}
                    onConfirmExisting={handleLinkExistingAccount}
                    onClose={() => setAssociationModal({ isOpen: false, doctor: null })}
                />
            )}
             {resetPwdModal.isOpen && (
                <ConfirmationModal title="Conferma Reset Password" onConfirm={() => handleResetPassword(resetPwdModal.doctor)} onClose={() => setResetPwdModal({isOpen: false, doctor: null})}>
                    <p>Sei sicuro di voler impostare la password di <b>{resetPwdModal.doctor.firstName}</b> a "123456"?</p><p>L'utente dovrà cambiarla al suo prossimo accesso.</p>
                </ConfirmationModal>
            )}

            <div className="management-container">
                <div className="management-header">
                    <h4>Gestione Medici Specialisti</h4>
                    {canEdit && ( <button onClick={handleNewClick} className="add-new-button">+ Aggiungi Medico</button> )}
                </div>

                <div className="filters-container">
                    <input type="text" placeholder="Cerca per nome e cognome..." className="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    <Select isMulti options={specialtyOptions} value={selectedSpecialties} onChange={setSelectedSpecialties} placeholder="Filtra per specialistiche..." className="specialty-select" classNamePrefix="react-select" noOptionsMessage={() => "Nessuna opzione"} />
                </div>

                <div className="management-list">
                    {filteredDoctors.map(doc => (
                        <div key={doc.id} className="management-card">
                            <div className="list-item-content">
                                <span className="list-item-main-text">{doc.lastName} {doc.firstName}</span>
                                {doc.pIva && <span className="list-item-sub-text">P. IVA: {doc.pIva}</span>}
                                
                                <div className="specialty-tags-container">
                                    {doc.specialtyNames.map(name => <span key={name} className="specialty-tag">{name}</span>)}
                                </div>
                                
                                <div style={{marginTop: '10px', fontSize: '0.85rem', display: 'flex', flexDirection: 'column', gap: '3px'}}>
                                    <div><b>Account App:</b> {doc.authUid ? <span style={{color: '#198754'}}>Associato</span> : <span style={{color: '#dc3545'}}>Non Associato</span>}</div>
                                    <div><b>Agenda:</b> {doc.isBookable !== false ? <span style={{color: '#0d6efd'}}>Prenotabile</span> : <span style={{color: '#6c757d'}}>Nascosto</span>}</div>
                                    <div><b>Prestazioni Assegnate:</b> {doc.prestazioniAssegnate?.length || 0}</div>
                                </div>
                                
                            </div>
                            <div className="list-item-actions" style={{width: '100%', display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                
                                {canEdit && (
                                    <>
                                        <div style={{display: 'flex', gap: '8px', width: '100%'}}>
                                            {/* 🐛 FIX: tolto il requisito "doc.email" — con la modalità "Associa
                                                Account Esistente" non serve più che il medico abbia già un'email
                                                impostata prima di poter cliccare qui. */}
                                            {!doc.authUid ? (
                                                <button style={{flex: 1}} onClick={() => setAssociationModal({ isOpen: true, doctor: doc })} className="duplicate-button">Crea Account</button>
                                            ) : (
                                                <>
                                                    <button style={{flex: 1}} onClick={() => handleChangeEmail(doc)} className="neutral-button" disabled={!doc.authUid}>Cambia Email</button>
                                                    <button style={{flex: 1}} onClick={() => setResetPwdModal({isOpen: true, doctor: doc})} className="warning-button" disabled={!doc.authUid}>Reset Password</button>
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}

                                <div style={{display: 'flex', gap: '8px', width: '100%'}}>
                                     {canEdit && <button style={{flex: 1}} onClick={() => handleEditClick(doc)} className="info-button">Modifica Dati</button>}
                                     {canDelete && <button style={{flex: 1}} onClick={() => handleDeleteDoctor(doc.id)} className="delete-button">Elimina</button>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}

export default ManageDoctors;